import React, { useState, useEffect } from 'react';
import { renderToString } from 'react-dom/server';
import {
  string,
  number,
  array,
  arrayOf,
  oneOfType,
  func,
  shape,
} from 'prop-types';
import classNames from 'classnames';
import {
  Map as YMap,
  YMaps,
  ZoomControl,
  GeolocationControl,
  FullscreenControl,
  ObjectManager
} from 'react-yandex-maps';

import { YANDEX_MAPS_API_KEY } from '../../constants';
import Zoom from './Zoom';
import { ZOOM_IN_BUTTON_ID, ZOOM_OUT_BUTTON_ID } from './Zoom/Zoom'
import FullScreen from './FullScreen';
import Geolocation from './Geolocation';
import Placemark from './Placemark';
import { BalloonContentLayout, BalloonLayout } from './Balloon';

import s from './index.module.css';

const cx = classNames.bind(s);

const DEFAULT_ZOOM = 11;
const MAX_ZOOM = 19;
const MIN_ZOOM = 10;

const getFeatures = (yMaps, items, onSelectLocation) => {
  if (!yMaps || !items || items.length === 0) {
    return {
      type: 'FeatureCollection',
      features: [],
    };
  }

  const features = items.map((item) => {
    const { sellerAddress, longitude, latitude, price, deliveryDays, quantity } = item;

    const iconLayout = yMaps.templateLayoutFactory.createClass(
      renderToString(<Placemark price={price} delivery={deliveryDays} />),
    );

    const onClick = () => {
      onSelectLocation(item);
    }

    const balloonLayout = BalloonLayout(yMaps, sellerAddress, onClick);
    const balloonContentLayout = BalloonContentLayout(yMaps.templateLayoutFactory, price, deliveryDays, quantity);

    return {
      type: 'Feature',
      id: item.key,
      geometry: { type: 'Point', coordinates: [latitude, longitude] },
      options: {
        iconLayout,
        iconPane: 'overlaps',
        balloonLayout,
        balloonContentLayout,
        balloonPane: 'controls',
        balloonAutoPanDuration: 0
      },
      properties: { ...item, },
    };
  });

  return {
    type: 'FeatureCollection',
    features,
  };
};

const Map = ({
  className,
  zoom,
  maxZoom,
  minZoom,
  centerCoordinates,
  selectedPin,
  onFullScreenEnter,
  onFullScreenExit,
  mapMargin,
  data,
  onSelectLocation,
}) => {
  const [latitude, longitude] = centerCoordinates;
  const [yMaps, setYMaps] = useState(null);
  const [objectManager, setObjectManager] = useState(null);

  const features = getFeatures(yMaps, data, onSelectLocation);

  const onBoundsChange = (event) => {
    const newZoom = event.get('newZoom');
    const oldZoom = event.get('oldZoom');

    if (newZoom !== oldZoom) {
      if (newZoom === maxZoom) {
        document.getElementById(ZOOM_IN_BUTTON_ID).setAttribute('disabled', true);
      } else {
        document.getElementById(ZOOM_IN_BUTTON_ID).removeAttribute('disabled');
      }

      if (newZoom === minZoom) {
        document.getElementById(ZOOM_OUT_BUTTON_ID).setAttribute('disabled', true);
      } else {
        document.getElementById(ZOOM_OUT_BUTTON_ID).removeAttribute('disabled');
      }
    }
  }

  useEffect(() => {
    if (objectManager && selectedPin) {
      const balloons = objectManager.objects && objectManager.objects.balloon;
      if (balloons) {
        // To avoid causing crash by calling ballons.open with invalid selectedPin - we check if selectedPin key is exists in current features (balloons) and have valid coordinates
        const currentFeatures = (features && features.features) || [];
        const matchedItem = currentFeatures.find(item => item.id === selectedPin);
        const matchedItemCoordinates = (matchedItem && matchedItem.geometry && matchedItem.geometry.coordinates) || [];
        const validCoordinates = matchedItemCoordinates.filter(value => value > 0);
        if (matchedItem && validCoordinates) {
          balloons.open(selectedPin);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objectManager, selectedPin]);

  return (
    <div className={cx(s.mapWrapper, { [className]: true })}>
      <YMaps query={{ apikey: YANDEX_MAPS_API_KEY }}>
        <YMap
          onBoundsChange={onBoundsChange}
          onLoad={(instance) => setYMaps(instance)}
          modules={['geolocation', 'templateLayoutFactory', 'objectManager.addon.objectsBalloon', 'geometry.pixel.Rectangle', 'shape.Rectangle']}
          defaultState={{ center: [latitude, longitude], zoom, margin: mapMargin }}
          options={{ suppressMapOpenBlock: true, maxZoom, minZoom, balloonPanelMaxMapArea: 0, balloonZIndex: 9999 }}
          style={{
            width: '100%',
            height: '100%',
          }}
        >
          <ObjectManager
            instanceRef={(ref) => setObjectManager(ref)}
            options={{
              clusterize: false,
            }}
            objects={{}}
            clusters={{}}
            features={features}
          />
          <Zoom>
            {({ template }) => (
              <ZoomControl
                height="100%"
                options={{ layout: template }}
              />
            )}
          </Zoom>
          <Geolocation>
            {({ template }) => (
              <GeolocationControl
                options={{ layout: template }}
              />
            )}
          </Geolocation>
          <FullScreen>
            {({ template }) => (
              <FullscreenControl
                onFullscreenenter={onFullScreenEnter}
                onFullscreenexit={onFullScreenExit}
                options={{ layout: template }}
              />
            )}
          </FullScreen>
        </YMap>
      </YMaps>
    </div>
  );
};

Map.propTypes = {
  className: string,
  centerCoordinates: array,
  selectedPin: string,
  maxZoom: number,
  minZoom: number,
  zoom: number,
  mapMargin: oneOfType([number, array]),
  onFullScreenEnter: func,
  onFullScreenExit: func,
  data: arrayOf(shape({
    key: string,
    sellerName: string,
    sellerAddress: string,
    longitude: number,
    latitude: number,
    price: number,
    deliveryDays: number,
    quantity: number,
    quantityLot: number,
  })),
};

Map.defaultProps = {
  longitude: 37.57,
  latitude: 55.75,
  maxZoom: MAX_ZOOM,
  minZoom: MIN_ZOOM,
  zoom: DEFAULT_ZOOM,
  className: null,
  mapMargin: 0,
  onFullScreenEnter: () => { },
  onFullScreenExit: () => { },
  data: [],
};

export default Map;